import { Formik } from "formik";
import { useEffect, useRef, useState, useMemo } from "react";
import * as yup from "yup";
import { Button } from "../../../components/ui/button";
import Card from "../../../components/ui/card-snippet";
import { useNavigate } from "react-router-dom";
import {
  respEnum,
  GenderTypeEnum,
  AppUserSettingsTypeEnum,
} from "../../../constants/appConstant";
import {
  FormikInput,
  FormikSwitch,
  FormikSelect,
} from "../../../components/formik-components";
import Common from "../../../helper/common";
import { useQueryParams } from "../../../contexts/queryParamsContext";
import AntDesignTree from "../../../components/antdesignTree/antdesignTree";

const formValidations = yup.object({
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required"),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  gender: yup.string().required("Gender is required"),
  password: yup.string().when("id", (id, schema) => {
    return id == 0
      ? schema
        .required("Password is required")
        .min(6, "Password must be at least 6 characters")
      : schema.notRequired();
  }),
  clubRoles: yup.array().min(1, "Please select at least one club and role"),

});

export default function AddClientUser() {
  const navigate = useNavigate();
  const formikRef = useRef();
  const [clubsnroles, setClubsnRoles] = useState([]);
  const { params, setParams } = useQueryParams();
  const [masterUser,setMasterUser]=useState();
  const id = params.userId ? parseInt(params.userId) : 0;

  useEffect(() => {
   
    getClubsnRoles();
    if (id) {
      getUserDetail();
    }

    return () => {
      setParams({
        ...params,
        userId: null,
      });
    };
  }, [id]);

// Dynamic initial values for settings
const createSettingsInitialValues = () => {
  const settingsValues = {};
  Object.keys(AppUserSettingsTypeEnum).forEach(key => {
    const fieldName = `${key.toLowerCase()}Hours`;
    settingsValues[fieldName] = ""; // No default value
  });
  return settingsValues;
};

const formInitialValue = {
  id: 0,
  firstName: "",
  lastName: "",
  email: "",
  gender:null,
  isactive: false,
  isVerified: false,
  password: "",
  clubRoles: [],
  ...createSettingsInitialValues(),
};

  const getClubsnRoles = async () => {
    const response = await Common.ApiService.getInstance().request(
      "GetClientnRoles",
      "GET"
    );
    if (response?.status == respEnum.Success && response?.data) {
      setClubsnRoles(response.data[0] || []);
    }
  };
  const processedTreeData = useMemo(() => {
    const markChildrenCheckable = (nodes) => {
      return nodes.map(node => {
        const hasChildren = node.children && node.children.length > 0;
        return {
          ...node,
          selectable: !hasChildren, // parents not selectable
          children: hasChildren ? markChildrenCheckable(node.children) : [],
        };
      });
    };

    return markChildrenCheckable(clubsnroles);
  }, [clubsnroles]);



  const getUserDetail = async () => {
    let response = await Common.ApiService.getInstance().request(
      `GetClientUserDetails?userId=${id}`
    );

    if (response.status == respEnum.Success && response?.data?.length > 0) {
      let userData = response.data[0];
      setMasterUser(userData.is_master_user);
      const selectedKeys = userData.clubroles?.map(item => item.roleid.toString()) || [];
      
      // Extract settings from appusersettings if available (note: lowercase from API)
      const settings = userData.appusersettings || [];
      const settingsData = {};
      Object.keys(AppUserSettingsTypeEnum).forEach(key => {
        const fieldName = `${key.toLowerCase()}Hours`;
        const settingValue = settings.find(s => s.type === AppUserSettingsTypeEnum[key])?.value || "";
        settingsData[fieldName] = settingValue;
      });
      
      if (formikRef.current) {
          formikRef.current.setValues({
          ...userData,
          password: "", // Clear password for security
          // Convert IDs to string format for select components
          roleId: userData.roleId?.toString(),
          gender: userData.gender?.toString(),
          clubRoles: selectedKeys,
          // Add settings data
          ...settingsData,
          });
      }
    }
  };

  const onSubmitData = async (data, action) => {
    const formData = {
      ...data,
      id: id || 0,
      // roleId: parseInt(data.roleId),
      gender: parseInt(data.gender),
    };

    // ✅ Transform clubRoles into [{ clubid, roleid }]
    const clubRolePairs = [];
    formData.clubRoles.forEach(selectedId => {
      // Find if selected ID is a club
      const club = clubsnroles.find(c => c.key === selectedId);
      if (club) {
        // Add all children roles under this club
        club.children?.forEach(role => {
          clubRolePairs.push({
            clubid: parseInt(club.key),
            roleid: parseInt(role.key),
          });
        });
      } else {
        // Otherwise it's a role ID — find its parent club
        clubsnroles.forEach(clubItem => {
          const role = clubItem.children?.find(r => r.key === selectedId);
          if (role) {
            clubRolePairs.push({
              clubid: parseInt(clubItem.key),
              roleid: parseInt(role.key),
            });
          }
        });
      }
    });

    formData.clubRoles = clubRolePairs;
    console.log(formData.clubRoles, "fallbacks")
    if (!formData.clubRoles.length) {
      Common.showToast("Please select at least one club and role", "Error", "error");
      return;
    }

    const appUserSettings = [];
    Object.keys(AppUserSettingsTypeEnum).forEach(key => {
      const fieldName = `${key.toLowerCase()}Hours`;
      const value = data[fieldName] ? parseInt(data[fieldName]) : null;
      if (value !== null) {
        appUserSettings.push({
          id: 0,
          type: AppUserSettingsTypeEnum[key],
          value: value
        });
      }
    });

    formData.appUserSettings = appUserSettings;

    console.log(formData, "payload to save")
    let response = await Common.ApiService.getInstance().request(
      "AddUpdateClientUser",
      formData,
      "POST"
    );
    action.setSubmitting(false);

    if (response.status == respEnum.Success) {
      Common.SweetAlert.alert(response.message);
      setTimeout(() => {
        navigate("/app/clientusers");
      }, 1000);
    } else {
      //Common.showToast(response.message, "Error", "error");
    }
  };

  const genderOptions = [
    { id: null, name: "Select Gender" },
    { id: GenderTypeEnum.Male.toString(), name: "Male" },
    { id: GenderTypeEnum.Female.toString(), name: "Female" },
    { id: GenderTypeEnum.Other.toString(), name: "Other" },
  ];

  return (
    <div className="card-holder">
      <Card title={id ? "Update User" : "Add User"}>
        <div className="p-5">
          <Formik
            innerRef={formikRef}
            validationSchema={formValidations}
            initialValues={formInitialValue}
            onSubmit={onSubmitData}
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize
          >
            {(formikProps) => (
              <form onSubmit={formikProps.handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
                  <FormikInput
                    label="First Name"
                    name="firstName"
                    placeholder="First Name"
                    formikProps={formikProps}
                  />
                  <FormikInput
                    label="Last Name"
                    name="lastName"
                    placeholder="Last Name"
                    formikProps={formikProps}
                  />
                  <FormikInput
                    label="Email"
                    name="email"
                    placeholder="Email Address"
                    formikProps={formikProps}
                    type="email"
                  />
                  <FormikSelect
                    label="Gender"
                    name="gender"
                    data={genderOptions}
                    formikProps={formikProps}
                  />
                  <FormikInput
                    label="Password"
                    name="password"
                    placeholder={
                      id ? "Leave blank to keep current password" : "Password"
                    }
                    formikProps={formikProps}
                    type="password"
                  />
                  <div className="flex items-end space-x-6">
                    {/* Wrap switches and button in a flex container with gap */}
                    <FormikSwitch
                      label="Active"
                      name="isactive"
                      formikProps={formikProps}
                      disabled={masterUser} //if master user disabled this option
                    />
                    <FormikSwitch
                      label="Verified"
                      name="isVerified"
                      formikProps={formikProps}
                      disabled={masterUser} //if master user disabled this option
                    />
                  </div>
                  <div className="col-span-2 mt-4">
                    <label className="block mb-2 font-medium text-gray-700">Select Roles</label>
                   
                    <AntDesignTree
                      treeData={processedTreeData}
                      value={formikProps.values.clubRoles}
                      onChange={(val) => formikProps.setFieldValue("clubRoles", val)}
                      onSelect={(selectedValue, node) => {
                        console.log(selectedValue, node, "treedata")
                        if (node?.key) {
                          debugger
                          // Get parent ID from node props
                          const parentId = node.key;

                          // Find parent in tree
                          const parentClub = processedTreeData.find(club =>
                            club.children?.some(child => child.key === parentId)
                          );

                          if (parentClub && parentClub.children.length > 1) {
                            // Remove all other children from selected list
                            let updated = formikProps.values.clubRoles.filter(
                              (val) => !parentClub.children.some((child) => child.key === val)
                            );

                            // Add only the newly selected child
                            updated.push(selectedValue);

                            formikProps.setFieldValue("clubRoles", updated);
                          }
                        }
                      }}
                      placeholder="Please select roles"
                    />
                  </div>
                  
                  {/* Settings Section - Compact */}
                  {/* <div className="col-span-2 mt-4">
                    <div className="border-t pt-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Settings (Hours)
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.keys(AppUserSettingsTypeEnum).map((key) => {
                          const fieldName = `${key.toLowerCase()}Hours`;
                          const label = Common.Utility.getKeyByValue(AppUserSettingsTypeEnum, AppUserSettingsTypeEnum[key]);
                          return (
                            <div key={key} className="flex items-center gap-3 p-2 bg-gray-50 border border-gray-200 rounded">
                              <div className="flex-1 min-w-0">
                                <span className="text-xs font-medium text-gray-600 truncate">{label}</span>
                              </div>
                              <div className="w-16">
                                <FormikInput
                                  name={fieldName}
                                  placeholder="Hrs"
                                  formikProps={formikProps}
                                  type="number"
                                  min="0"
                                  className="text-center text-xs"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div> */}
               
                </div>

                <div className="flex justify-end mt-6 space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/app/clientusers")}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={formikProps.isSubmitting}>
                    {formikProps.isSubmitting
                      ? "Saving..."
                      : id
                        ? "Update"
                        : "Save"}
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </Card>
    </div>
  );
}